import { useEffect } from 'react';
import { Outlet, useLocation, Navigate } from 'react-router-dom';
import TopBar from '../components/TopBar';
import Sidebar from '../components/Sidebar';
import { CoiModal, InvoiceDocModal, NegotiateModal, EsignModal } from '../components/Modals';
import { WelcomeModal, TourOverlay, ChecklistWidget } from '../components/Ftue';
import { useApp } from '../state/AppContext';
import { screenFromPath, titlesFor } from '../lib/roles';
import type { ScreenKey } from '../lib/types';

export default function AppLayout() {
  const location = useLocation();
  const { state, set } = useApp();
  const screen = screenFromPath(location.pathname);

  // Close the mobile drawer whenever the route changes (nav tap, deep link).
  useEffect(() => { set({ sidebarOpen: false }); }, [location.pathname, set]);

  // The demo is gated: a deep link into /app without clearing the password
  // curtain goes to the login screen (see src/lib/demoAuth.ts).
  if (!state.authed) return <Navigate to="/login" replace />;

  // /app or an unknown sub-path → land on the dashboard.
  if (!screen) return <Navigate to="/app/dashboard" replace />;

  const [breadcrumb, title, subtitle] = titlesFor(state.lang, screen);

  return (
    <div style={{ height: '100vh', display: 'flex', flexDirection: 'column', fontFamily: 'var(--font-sans)', color: 'var(--color-fg-1)', overflow: 'hidden' }}>
      <TopBar />
      <div style={{ flex: 1, display: 'flex', minHeight: 0 }}>
        <Sidebar current={screen as ScreenKey} open={state.sidebarOpen} />
        <div
          className={'app-scrim' + (state.sidebarOpen ? ' show' : '')}
          onClick={() => set({ sidebarOpen: false })}
          aria-hidden
        />
        <main data-tour="main" style={{ flex: 1, minWidth: 0, overflow: 'auto', background: 'var(--color-mist)' }}>
          <div key={screen} className="aibid-fade app-main-inner" style={{ padding: '24px clamp(24px,4vw,64px)', maxWidth: 1440, margin: '0 auto' }}>
            <div style={{ fontSize: 12, color: 'var(--color-fg-3)', marginBottom: 6, fontWeight: 500 }}>{breadcrumb}</div>
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, marginBottom: 22 }}>
              <div>
                <h3 style={{ fontSize: 24, fontWeight: 700, marginBottom: 4 }}>{title}</h3>
                <div style={{ fontSize: 14, color: 'var(--color-fg-3)' }}>{subtitle}</div>
              </div>
            </div>
            <Outlet />
          </div>
        </main>
      </div>

      {/* Global overlays — mounted for the whole app surface */}
      <CoiModal />
      <InvoiceDocModal />
      <NegotiateModal />
      <EsignModal />
      <WelcomeModal />
      <TourOverlay />
      <ChecklistWidget />
    </div>
  );
}
